import React, { useEffect, useState, useMemo, useCallback } from "react";
import "./PaiementTable.scss";
// import DeleteConfirmation from "../DeleteConfirmation/DeleteConfirmation";
// import EditUserForm from "../EditUserForm/EditUserForm";
import { useDispatch, useSelector } from "react-redux";
import { getPaginatedPayments } from "../../Reducers/actions/paymentActions";
import { HykTableLoadingSkeleton } from "../Skeletons/BatchScreenSkeletons";
import { getUserDisplayName } from "../../../helpers/userDisplayName";

const PAYMENTS_PER_PAGE = 10;
const FETCH_LIMIT = 1000;

// Sort configuration constants
const SORT_DIRECTIONS = {
    ASC: 'asc',
    DESC: 'desc'
};

// Column configuration for better maintainability
const TABLE_COLUMNS = [
    { key: 'id', label: 'Payment ID' },
    { key: 'seller_id', label: 'Seller ID' },
    { key: 'seller_name', label: 'Seller Name' },
    { key: 'buyer_id', label: 'Buyer ID' },
    { key: 'buyer_name', label: 'Buyer Name' },
    { key: 'creation_title', label: 'Creation Title' },
    { key: 'payment_date', label: 'Payment Date' },
    { key: 'amount', label: 'Amount' },
    { key: 'status', label: 'Status' }
];

const PaiementTable = () => {
    const dispatch = useDispatch();

    // Redux state
    const { payments, loading } = useSelector((state) => ({
        payments: state?.payments?.payments || [],
        loading: state?.payments?.loading || false,
    }));

    // Local state
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: SORT_DIRECTIONS.ASC });
    const [searchQuery, setSearchQuery] = useState('');
    const [isMobile, setIsMobile] = useState(false);

    // Check if device is mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Fetch payments on component mount
    useEffect(() => {
        dispatch(getPaginatedPayments(0, FETCH_LIMIT, null, true));
    }, [dispatch]);

    // Helper function to get sort value for a payment
    const getSortValue = useCallback((payment, key) => {
        switch (key) {
            case 'id':
                return payment.id;
            case 'seller_id':
                return payment?.beneficiary?.user_id || '';
            case 'seller_name':
                return getUserDisplayName(payment?.beneficiary)?.toLowerCase();
            case 'buyer_id':
                return payment?.grantor?.user_id || '';
            case 'buyer_name':
                return getUserDisplayName(payment?.grantor)?.toLowerCase();
            case 'creation_title':
                return payment?.creation?.title?.toLowerCase() || '';
            case 'payment_date':
                return new Date(payment?.created_at);
            case 'amount':
                return payment?.amount || 0;
            case 'status':
                return payment.status?.toLowerCase() || '';
            default:
                return '';
        }
    }, []);

    // Search matching function
    const matchesSearchQuery = useCallback((payment, query) => {
        const searchLower = query.toLowerCase();
        const formattedDate = new Date(payment?.created_at).toLocaleDateString("en-GB", {
            year: "numeric",
            month: "short",
            day: "2-digit",
        }).toLowerCase();

        return (
            payment.id?.toString().includes(searchLower) ||
            payment?.beneficiary?.user_id?.toString().includes(searchLower) ||
            getUserDisplayName(payment?.beneficiary)?.toLowerCase().includes(searchLower) ||
            payment?.grantor?.user_id?.toString().includes(searchLower) ||
            getUserDisplayName(payment?.grantor)?.toLowerCase().includes(searchLower) ||
            payment?.creation?.title?.toLowerCase().includes(searchLower) ||
            payment?.creation?.creation_id?.toString().includes(searchLower) ||
            payment.status?.toLowerCase().includes(searchLower) ||
            payment?.currency?.toLowerCase().includes(searchLower) ||
            payment?.amount?.toString().includes(searchLower) ||
            formattedDate.includes(searchLower)
        );
    }, []);

    // Event handlers
    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === SORT_DIRECTIONS.ASC
                ? SORT_DIRECTIONS.DESC
                : SORT_DIRECTIONS.ASC
        }));
    }, []);

    const handleSearch = useCallback((event) => {
        setSearchQuery(event.target.value);
        setCurrentPage(1); // Reset to first page when searching
    }, []);

    const handlePageChange = useCallback((pageNumber) => {
        setCurrentPage(pageNumber);
    }, []);

    // Memoized computed values
    const filteredPayments = useMemo(() => {
        if (!searchQuery.trim()) return payments;
        return payments.filter(payment => matchesSearchQuery(payment, searchQuery));
    }, [payments, searchQuery, matchesSearchQuery]);

    const sortedPayments = useMemo(() => {
        if (!sortConfig.key || !filteredPayments) return filteredPayments;

        return [...filteredPayments].sort((a, b) => {
            const aValue = getSortValue(a, sortConfig.key);
            const bValue = getSortValue(b, sortConfig.key);

            if (aValue < bValue) {
                return sortConfig.direction === SORT_DIRECTIONS.ASC ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === SORT_DIRECTIONS.ASC ? 1 : -1;
            }
            return 0;
        });
    }, [filteredPayments, sortConfig, getSortValue]);

    const paginationData = useMemo(() => {
        const indexOfLastPayment = currentPage * PAYMENTS_PER_PAGE;
        const indexOfFirstPayment = indexOfLastPayment - PAYMENTS_PER_PAGE;
        const currentPayments = sortedPayments.slice(indexOfFirstPayment, indexOfLastPayment);
        const totalPages = Math.ceil(sortedPayments.length / PAYMENTS_PER_PAGE);

        return {
            currentPayments,
            totalPages,
            hasMultiplePages: totalPages > 1
        };
    }, [sortedPayments, currentPage]);

    // Helper function to get sort icon
    const getSortIcon = useCallback((columnKey) => {
        if (sortConfig.key !== columnKey) return '↕';
        return sortConfig.direction === SORT_DIRECTIONS.ASC ? '↑' : '↓';
    }, [sortConfig]);

    // Helper function to format payment date
    const formatPaymentDate = useCallback((dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            year: "numeric",
            month: "short",
            day: "2-digit",
        });
    }, []);

    // Helper function to format payment amount
    const formatPaymentAmount = useCallback((amount, currency) => {
        return `${(amount || 0) / 100} ${currency}`;
    }, []);

    // Mobile card view component
    const MobilePaymentCard = ({ payment }) => (
        <div className="mobile-table-card">
            <div className="card-header">
                <div className="card-title">
                    Payment #{payment.id}
                </div>
                <div className={`status-badge status-${payment.status.toLowerCase()}`}>
                    {payment.status}
                </div>
            </div>
            <div className="card-content">
                <div className="content-row">
                    <span className="label">Seller:</span>
                    <span className="value">{getUserDisplayName(payment?.beneficiary)}</span>
                </div>
                <div className="content-row">
                    <span className="label">Buyer:</span>
                    <span className="value">{getUserDisplayName(payment?.grantor)}</span>
                </div>
                <div className="content-row">
                    <span className="label">Creation:</span>
                    <span className="value">
                        <a
                            href={`/creations/${payment?.creation?.creation_id}`}
                            className="creation-link"
                        >
                            {payment?.creation?.title}
                        </a>
                    </span>
                </div>
                <div className="content-row">
                    <span className="label">Amount:</span>
                    <span className="value">{formatPaymentAmount(payment?.amount, payment?.currency)}</span>
                </div>
                <div className="content-row">
                    <span className="label">Date:</span>
                    <span className="value">{formatPaymentDate(payment?.created_at)}</span>
                </div>
            </div>
        </div>
    );

    // Loading state
    if (loading) {
        return <HykTableLoadingSkeleton />;
    }

    // Empty state
    if (!payments || payments.length === 0) {
        return (
            <div className="hyk-table">
                <div className="empty-state">
                    No payments found.
                </div>
            </div>
        );
    }

    return (
        <div className="hyk-table">
            {/* Search Header */}
            <div className="table-header">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search payments..."
                        value={searchQuery}
                        onChange={handleSearch}
                    />
                    {searchQuery && (
                        <span className="search-count">
                            {filteredPayments.length} results
                        </span>
                    )}
                </div>
            </div>

            {isMobile ? (
                // Mobile card view
                <div className="mobile-payments-list">
                    {paginationData.currentPayments.map((payment) => (
                        <MobilePaymentCard key={payment.id} payment={payment} />
                    ))}
                </div>
            ) : (
                // Desktop table view
                <div className="table-wrapper">
                <table>
                    <thead>
                        <tr>
                            {TABLE_COLUMNS.map(({ key, label }) => (
                                <th
                                    key={key}
                                    onClick={() => handleSort(key)}
                                    className="sortable"
                                >
                                    {label} {getSortIcon(key)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paginationData.currentPayments.map((payment) => (
                            <tr key={payment.id}>
                                <td>{payment.id}</td>
                                <td>{payment?.beneficiary?.user_id}</td>
                                <td>{getUserDisplayName(payment?.beneficiary)}</td>
                                <td>{payment?.grantor?.user_id}</td>
                                <td>{getUserDisplayName(payment?.grantor)}</td>
                                <td>
                                    <a
                                        href={`/creations/${payment?.creation?.creation_id}`}
                                        className="creation-link"
                                    >
                                        {payment?.creation?.title}
                                    </a>
                                </td>
                                <td>{formatPaymentDate(payment?.created_at)}</td>
                                <td>{formatPaymentAmount(payment?.amount, payment?.currency)}</td>
                                <td className={`status-${payment.status.toLowerCase()}`}>
                                    {payment.status}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            )}

            {/* Pagination */}
            {paginationData.hasMultiplePages && (
                <div className="pagination">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <span>
                        Page {currentPage} of {paginationData.totalPages} (Total: {sortedPayments.length} payments)
                    </span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === paginationData.totalPages}
                    >
                        Next
                    </button>
                </div>
            )}

            {/* No Search Results */}
            {searchQuery && filteredPayments.length === 0 && (
                <div className="empty-state">
                    No payments found matching "{searchQuery}".
                </div>
            )}
        </div>
    );
};

export default PaiementTable;
